import { useEffect, FunctionComponent, useState, useCallback } from 'react';
import { logError, logInfo } from '@bonanza-social/toolkit/logging';
import { selectAccount } from '@bonanza-social/account/state';
import { useSelector } from '@bonanza-social/toolkit/state';
import { Loader } from '@bonanza-social/moonlight';
import { loadMkodoScript } from '../utils/mkodo';
import { MGeoJSGatewayStatus, MGeoJSPermissionStatus } from '../state';
import { apiConfig } from '@bonanza-social/toolkit/api';
import { getSessionId } from '@bonanza-social/web/src/utils';

const COMMON_STYLES = { flex: 1, paddingHorizontal: 'space-600' } as const;

const withMkodoCheck = <T extends object>(Component: FunctionComponent<T>) => {
  function MkodoCheck(props: T) {
    const [gatewayStatus, setGatewayStatus] = useState<MGeoJSGatewayStatus>();
    const [permissionStatus, setPermissionStatus] =
      useState<MGeoJSPermissionStatus>();

    const retrievePermissionStatus = useCallback(
      () =>
        new Promise<MGeoJSPermissionStatus>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            () => {
              resolve('GRANTED');
            },
            (error) => {
              resolve(
                error.code === error.PERMISSION_DENIED
                  ? 'DENIED'
                  : 'UNAVAILABLE',
              );
            },
            {
              enableHighAccuracy: false,
              maximumAge: 60 * 60 * 1000, // 1 hour
            },
          );
        }),
      [],
    );

    const initMkodo = useCallback(async () => {
      try {
        await loadMkodoScript();

        const permissionStatus = await retrievePermissionStatus();

        setPermissionStatus(permissionStatus);

        if (!window.mGeoJS || permissionStatus !== 'GRANTED') {
          return;
        }

        const listenerTokens = [
          window.mGeoJS.subscribe('onPingResponse', ({ gatewayStatus }) => {
            setGatewayStatus(gatewayStatus);
          }),

          window.mGeoJS.subscribe('outOfRegion', () => {
            logInfo('Mkodo', 'outOfRegion');

            setGatewayStatus('OutOfRegion');
          }),

          window.mGeoJS.subscribe('noGeoLocation', (status) => {
            logError('Mkodo error', {
              type: 'noGeoLocation',
              data: { status },
            });

            setPermissionStatus(status === 'DENIED' ? 'DENIED' : 'UNAVAILABLE');
          }),

          window.mGeoJS.subscribe('maxRetriesExceeded', () => {
            logError('Mkodo error', { type: 'maxRetriesExceeded' });

            setPermissionStatus('UNAVAILABLE');
          }),
        ];

        window.mGeoJS.start({ ping: true });

        const userId = (await apiConfig.getUserId())!;
        const sessionId = getSessionId();
        window.mGeoJS.setIdentitiesInVault({
          userId,
          sessionId,
        });

        return listenerTokens;
      } catch (error) {
        logError('Mkodo error', error);
      }
    }, [retrievePermissionStatus]);

    useEffect(() => {
      const listenerTokensPromise = initMkodo();

      return () => {
        window.mGeoJS?.stop();

        listenerTokensPromise.then((tokens) => {
          if (tokens) {
            tokens.forEach((token) => {
              window.mGeoJS?.unsubscribe(token);
            });
          }
        });
      };
    }, [initMkodo]);

    if (!gatewayStatus || !permissionStatus) {
      return <Loader {...COMMON_STYLES} />;
    }

    return <Component {...props} />;
  }

  return function WhitelistedCheck(props: T) {
    const { isMkodoWhitelisted } = useSelector(selectAccount) || {};

    return isMkodoWhitelisted ? (
      <Component {...props} />
    ) : (
      <MkodoCheck {...props} />
    );
  };
};

export default withMkodoCheck;
